/**
 * CallLogsReportTable — detailed view for the Call Logs report.
 *
 * Searchable, sortable, paginated read-only table. Columns:
 *   Call Start | Duration | Call Type | Event | Caller | Receiver
 *
 * Link rules:
 *   - Event   : /profiles/event/{event_id}  (when event_id present)
 *   - Caller  : /profiles/user/{caller_id}  (only when caller_type_id === 2)
 *   - Receiver: by callee_type_id —
 *       1 → /profiles/area/{area_id}       (area_id surfaced by the proc)
 *       2 → /profiles/user/{callee_id}
 *       3 → /profiles/resident/{callee_id}
 *     anything else → plain text
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import ActiveFilterBar from '../../../community/residents/components/ActiveFilterBar.jsx';

// Per-column filters supported on this table.
const FILTER_COLUMN_LABELS = {
    call_type_name: 'Call Type',
    caller: 'Caller',
    receiver: 'Receiver',
};

// callee_type_id → profile route builder. Returns null when no link applies.
const buildReceiverLink = (row) => {
    const type = parseInt(row.callee_type_id, 10);
    if (type === 1 && row.area_id) return `/profiles/area/${row.area_id}`;
    if (type === 2 && row.callee_id) return `/profiles/user/${row.callee_id}`;
    if (type === 3 && row.callee_id) return `/profiles/resident/${row.callee_id}`;
    return null;
};

const CallLogsReportTable = ({ records, tzOffset }) => {
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({}); // { columnKey: string[] }
    const [sortCol, setSortCol] = useState('call_start_time');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // Unique values for a filterable column, derived from the FULL record set
    // (not the search/filter-narrowed view) so options never disappear.
    const getFilterValues = useCallback((columnKey) => {
        const values = new Set();
        (records || []).forEach((row) => {
            const val = row[columnKey];
            values.add(val != null && val !== '' ? String(val) : '(empty)');
        });
        return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [records]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
        setPage(1);
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({ ...prev, [columnKey]: (prev[columnKey] || []).filter((v) => v !== value) }));
        setPage(1);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({});
        setPage(1);
    }, []);

    const filtered = useMemo(() => {
        let rows = [...(records || [])];

        if (search) {
            const q = search.toLowerCase();
            rows = rows.filter((r) =>
                (r.call_type_name || '').toLowerCase().includes(q) ||
                (r.caller || '').toLowerCase().includes(q) ||
                (r.receiver || '').toLowerCase().includes(q) ||
                (r.event_id || '').toString().toLowerCase().includes(q) ||
                (r.call_length || '').toString().toLowerCase().includes(q) ||
                (r.call_start_time || '').toLowerCase().includes(q)
            );
        }

        // Per-column filters (Call Type / Caller / Receiver)
        Object.entries(filters).forEach(([columnKey, vals]) => {
            if (!vals || vals.length === 0) return;
            rows = rows.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return vals.includes(strVal);
            });
        });

        rows.sort((a, b) => {
            const aVal = (a[sortCol] || '').toString().toLowerCase();
            const bVal = (b[sortCol] || '').toString().toLowerCase();
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return rows;
    }, [records, search, filters, sortCol, sortDir]);

    // Pagination math (mirrors AlertDetailsPage's ap-pagination)
    const totalFiltered = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, totalFiltered);
    const pageRows = filtered.slice(startIdx, endIdx);
    const showingStart = totalFiltered === 0 ? 0 : startIdx + 1;
    const showingEnd = endIdx;

    // Sliding window of up to 5 page numbers, centered on the current page.
    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxVisible = 5;
        let s = Math.max(1, safePage - Math.floor(maxVisible / 2));
        const e = Math.min(totalPages, s + maxVisible - 1);
        if (e - s + 1 < maxVisible) s = Math.max(1, e - maxVisible + 1);
        for (let i = s; i <= e; i++) pages.push(i);
        return pages;
    }, [safePage, totalPages]);

    const handleSort = useCallback((col) => {
        if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    }, [sortCol]);

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>;
        return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <h3 className="pp-section__title">Call Records</h3>
                <div className="pp-section__actions">
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                        {filtered.length} {filtered.length === 1 ? 'call' : 'calls'}
                    </span>
                </div>
            </div>
            <div className="pp-section__body">
                <div className="dp-controls">
                    <div className="dp-controls__left">
                        <div className="dp-search">
                            <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                            <input
                                className="dp-search__input"
                                type="text"
                                placeholder="Search calls…"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>
                    </div>
                </div>

                {/* Active filter chips */}
                <ActiveFilterBar
                    filters={filters}
                    columnLabels={FILTER_COLUMN_LABELS}
                    onRemoveFilter={handleRemoveFilter}
                    onClearAll={handleClearAllFilters}
                />

                {(records || []).length === 0 ? (
                    <div className="dp-empty" style={{ marginTop: 'var(--space-4)' }}>
                        <p className="dp-empty__title">No calls</p>
                        <p className="dp-empty__text">No calls were recorded for the selected period.</p>
                    </div>
                ) : (
                    <>
                        <div className="dp-table-wrap" style={{ marginTop: 'var(--space-3)' }}>
                            <table className="dp-table">
                                <thead className="dp-thead">
                                    <tr>
                                        <th className="dp-th"><div className="dp-th__content"><button className="dp-th__sort-btn" onClick={() => handleSort('call_start_time')}>Call Start <SortIcon col="call_start_time" /></button></div></th>
                                        <th className="dp-th"><div className="dp-th__content"><button className="dp-th__sort-btn" onClick={() => handleSort('call_length')}>Duration <SortIcon col="call_length" /></button></div></th>
                                        <th className="dp-th"><div className="dp-th__content">
                                            <button className="dp-th__sort-btn" onClick={() => handleSort('call_type_name')}>Call Type <SortIcon col="call_type_name" /></button>
                                            <FilterPopover columnKey="call_type_name" label="Call Type" values={getFilterValues('call_type_name')} selectedValues={filters.call_type_name || []} onChange={handleFilterChange} />
                                        </div></th>
                                        <th className="dp-th"><div className="dp-th__content"><button className="dp-th__sort-btn" onClick={() => handleSort('event_id')}>Event <SortIcon col="event_id" /></button></div></th>
                                        <th className="dp-th"><div className="dp-th__content">
                                            <button className="dp-th__sort-btn" onClick={() => handleSort('caller')}>Caller <SortIcon col="caller" /></button>
                                            <FilterPopover columnKey="caller" label="Caller" values={getFilterValues('caller')} selectedValues={filters.caller || []} onChange={handleFilterChange} />
                                        </div></th>
                                        <th className="dp-th"><div className="dp-th__content">
                                            <button className="dp-th__sort-btn" onClick={() => handleSort('receiver')}>Receiver <SortIcon col="receiver" /></button>
                                            <FilterPopover columnKey="receiver" label="Receiver" values={getFilterValues('receiver')} selectedValues={filters.receiver || []} onChange={handleFilterChange} />
                                        </div></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((row, idx) => {
                                        // The proc resolves `caller` (display_name) only by joining
                                        // core_user on caller_id, so a resolved caller name means the
                                        // caller is a user — link it to the user profile by caller_id.
                                        const callerLinkable = Boolean(row.caller_id) && Boolean(row.caller);
                                        const receiverLink = buildReceiverLink(row);
                                        return (
                                            <tr key={row.call_id || idx} className="dp-tr">
                                                <td className="dp-td"><span className="dp-cell-text dp-cell-text--muted" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{convertIsoUTCToLocal(row.call_start_time, tzOffset) || '—'}</span></td>
                                                <td className="dp-td"><span className="dp-cell-text" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{row.call_length || '—'}</span></td>
                                                <td className="dp-td"><span className="dp-cell-text">{row.call_type_name || '—'}</span></td>
                                                <td className="dp-td">
                                                    {row.event_id ? (
                                                        <Link to={`/profiles/event/${row.event_id}`} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>{row.event_id}</Link>
                                                    ) : (
                                                        <span className="dp-cell-text dp-cell-text--muted">—</span>
                                                    )}
                                                </td>
                                                <td className="dp-td">
                                                    {callerLinkable ? (
                                                        <Link to={`/profiles/user/${row.caller_id}`} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>{row.caller}</Link>
                                                    ) : (
                                                        <span className="dp-cell-text">{row.caller || '—'}</span>
                                                    )}
                                                </td>
                                                <td className="dp-td">
                                                    {row.receiver && receiverLink ? (
                                                        <Link to={receiverLink} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>{row.receiver}</Link>
                                                    ) : (
                                                        <span className="dp-cell-text">{row.receiver || '—'}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination — matches AlertDetailsPage's ap-pagination */}
                        <div className="ap-pagination">
                            <p className="ap-pagination__info">
                                Showing <span className="ap-pagination__emphasis">{showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{showingEnd}</span> of{' '}
                                <span className="ap-pagination__emphasis">{totalFiltered}</span> {totalFiltered === 1 ? 'call' : 'calls'}
                            </p>
                            <div className="ap-pagination__controls">
                                <div className="ap-per-page">
                                    <label htmlFor="cl-per-page" className="ap-per-page__label">Rows per page:</label>
                                    <select
                                        id="cl-per-page"
                                        value={perPage}
                                        onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }}
                                        className="ap-per-page__select"
                                    >
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={200}>200</option>
                                        <option value={300}>300</option>
                                    </select>
                                </div>
                                <nav className="ap-page-nav" aria-label="Call logs pagination">
                                    <button
                                        type="button"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={safePage <= 1}
                                        className="ap-page-btn"
                                        aria-label="Previous page"
                                    >
                                        <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                        </svg>
                                    </button>
                                    {pageNumbers.map((num) => (
                                        <button
                                            key={num}
                                            type="button"
                                            onClick={() => setPage(num)}
                                            className={`ap-page-num${num === safePage ? ' is-active' : ''}`}
                                            aria-label={`Page ${num}`}
                                            aria-current={num === safePage ? 'page' : undefined}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={safePage >= totalPages}
                                        className="ap-page-btn"
                                        aria-label="Next page"
                                    >
                                        <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                        </svg>
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CallLogsReportTable;
